import React, {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { translate } from '../../Services/translation';
import Input from '../Inputs/Input';
import { getPriceLabels } from '../../Actions/PriceLabel.action';
import PriceLabelModel from '../../Types/MyPages/PriceLabelModel';
import { createPortal } from 'react-dom';
import { useReactToPrint } from 'react-to-print';
import clsx from 'clsx';
import { scrollTopToElement } from '../../Services/Scroll.service';

const MyPagesPriceLabels = () => {
  const [articleNumbers, setArticleNumbers] = useState('');
  const [searchError, setSearchError] = useState(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [priceLabelModel, setPriceLabelModel] =
    useState<PriceLabelModel>();

  const submitSearch = async () => {
    setIsLoading(true);

    const data = await getPriceLabels(articleNumbers);
    const result = data as PriceLabelModel;
    setPriceLabelModel(result);
    setIsLoading(false);
  };

  useEffect(() => {
    if (priceLabelModel) {
      scrollTopToElement('price-labels-container');
    }
  }, [priceLabelModel]);

  const labelsRef = useRef(null);

  const onPrintClick = useReactToPrint({
    content: () => labelsRef.current,
    pageStyle: `
      html {
        zoom: 84%;
        padding: 0 !important;
        margin: 0 !important;
      }
      body {
        padding: 0 !important;
        margin: 0 !important;
      }
      @media print {
        @page {
          size: A4 landscape;
          margin: 0 !important;
        }
      }
    `,
  });

  const groupedPriceLabels = useMemo(
    () =>
      priceLabelModel?.barcodeItems
        ? groupArrayByCount(priceLabelModel?.barcodeItems, 33)
        : [],
    [priceLabelModel?.barcodeItems]
  );

  return (
    <div className="col-span-2">
      <h1 className="mb-2">{translate('mypage.pricelabels.title')}</h1>
      <p className="mb-4 max-w-3xl text-lg lg:mb-6">
        {translate('mypage.pricelabels.description')}
      </p>

      {searchError && <p className="error">{searchError}</p>}

      <form className="mb-12">
        <div className="grid grid-cols-1 gap-2">
          <Input
            inputSize="large"
            label={translate('mypage.pricelabels.articlenumbers')}
            wrapperClassName="[&_.input]:text-lg"
            type="text"
            value={articleNumbers}
            disabled={isLoading}
            onChange={(e) => setArticleNumbers(e.target.value)}
            style={{ resize: 'both' }}
            required
          />
        </div>
        <button
          className="btn btn--large btn--primary mt-2 lg:mt-4"
          disabled={isLoading || !articleNumbers}
          onClick={submitSearch}
        >
          {translate('form.pricelabel.search')}
        </button>
      </form>

      {priceLabelModel &&
        priceLabelModel.barcodeItems &&
        priceLabelModel.barcodeItems.length > 0 &&
        createPortal(
          <div>
            <div className="mb-4 flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <h3>{priceLabelModel.title}</h3>
                <button
                  className="btn btn--large btn--primary"
                  onClick={onPrintClick}
                >
                  {translate('general.save')}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div>{translate('mypage.pricelabel.edit-tips')}</div>
              </div>
            </div>
            <div ref={labelsRef} className="print:px-6" id="wrapper">
              {groupedPriceLabels.map((group, groupIdx) => (
                <Fragment key={`${groupIdx}-price-label-group`}>
                  <div
                    className="grid grid-cols-2 print:grid-cols-11 print:pt-10 md:grid-cols-5 lg:grid-cols-11"
                    style={{ pageBreakAfter: 'always' }}
                  >
                    {group.map((item, idx) => (
                      <div
                        key={`${groupIdx}-${item.articleNumber}-${idx}`}
                        className={clsx(
                          'flex flex-col border-2 border-dashed border-gray-300 last:!border-r-2 [&:not(:nth-child(11n))]:border-r-0 [&:nth-child(n+12)]:border-t-0',
                          groupIdx > 0 && '[@media_not_print]:border-t-0'
                        )}
                      >
                        <div className="flex flex-1 flex-col">
                          <div className="flex h-36 items-center justify-center border-b border-dotted border-gray-300">
                            <div
                              style={{
                                fontFamily: 'Barcode',
                                fontSize: 50,
                              }}
                              className="-rotate-90"
                            >
                              {item.barCodeString}
                            </div>
                          </div>
                          <div className="flex h-[114px] flex-col justify-between border-b-2 border-solid border-black">
                            <span
                              className="overflow-hidden break-words px-1.5 py-1 text-sm"
                              contentEditable
                              suppressContentEditableWarning={true}
                            >
                              {item.productName}
                            </span>
                            <span className="text-center text-sm">
                              {item.articleNumber}
                            </span>
                          </div>
                        </div>
                        <div
                          className="h-[30px] text-center"
                          style={{ fontSize: 20 }}
                          contentEditable
                          suppressContentEditableWarning={true}
                        >
                          {item.price}
                        </div>
                      </div>
                    ))}
                  </div>
                </Fragment>
              ))}
            </div>
          </div>,
          document.getElementById('price-labels-container')!
        )}
    </div>
  );
};

function groupArrayByCount(arr: any[], count: number) {
  const grouped: any[][] = [];

  for (let i = 0; i < arr.length; i += count) {
    grouped.push(arr.slice(i, i + count));
  }

  return grouped;
}

export default MyPagesPriceLabels;
